"use client"

import { CreditCard, Trash2, Star, StarOff } from "lucide-react"
import { useTranslations } from "next-intl"
import { useEffect, useState } from "react"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { useApiAction, createApiAction } from "@/hooks/useApiAction"
import { useToast } from "@/hooks/use-toast"
import { API_ROUTES } from "@/routes"
import type Stripe from "stripe"

interface PaymentMethodSelectorProps {
  selectedPaymentMethodId?: string
  onPaymentMethodSelect?: (paymentMethodId: string) => void
  onPaymentMethodRemove?: (paymentMethodId: string) => void
  showRemoveButton?: boolean
  showDefaultButton?: boolean
  horizontal?: boolean
}

export function PaymentMethodSelector({
  selectedPaymentMethodId,
  onPaymentMethodSelect,
  onPaymentMethodRemove,
  showRemoveButton = false,
  showDefaultButton = false,
  horizontal = false,
}: PaymentMethodSelectorProps) {
  const t = useTranslations("customer")
  const { toast } = useToast()
  const [paymentMethods, setPaymentMethods] = useState<Stripe.PaymentMethod[]>([])
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // API hooks
  const { apiActionSync: getPaymentMethods } = useApiAction<undefined, { paymentMethods: Stripe.PaymentMethod[] }>(
    API_ROUTES.paymentMethods,
    "GET",
    { showToast: false }
  )

  const { apiActionSync: getDefaultPaymentMethod } = useApiAction<undefined, { defaultPaymentMethodId: string | null }>(
    API_ROUTES.paymentMethodsDefault,
    "GET",
    { showToast: false }
  )

  const { apiActionSync: setDefaultPaymentMethod } = useApiAction<{ paymentMethodId: string }, undefined>(
    API_ROUTES.paymentMethodsDefault,
    "POST"
  )

  // Create API action for dynamic endpoints
  const removePaymentMethodAction = createApiAction<undefined, undefined>(API_ROUTES.paymentMethods, "DELETE", {
    showToast: false,
  })

  useEffect(() => {
    loadPaymentMethods()
    loadDefaultPaymentMethod()
  }, [])

  const loadPaymentMethods = async () => {
    setIsLoading(true)
    getPaymentMethods(undefined, (res) => {
      if (res?.success && res.data) {
        setPaymentMethods(res.data.paymentMethods)
      } else {
        toast({
          title: t("payment_methods.load_error.title"),
          description: t("payment_methods.load_error.description"),
          variant: "destructive",
        })
      }
      setIsLoading(false)
    })
  }

  const loadDefaultPaymentMethod = async () => {
    getDefaultPaymentMethod(undefined, (res) => {
      if (res?.success && res.data) {
        setDefaultPaymentMethodId(res.data.defaultPaymentMethodId)
      }
    })
  }

  const handlePaymentMethodSelect = (paymentMethodId: string) => {
    if (onPaymentMethodSelect) {
      onPaymentMethodSelect(paymentMethodId)
    }
  }

  const handlePaymentMethodRemove = async (paymentMethodId: string) => {
    const res = await removePaymentMethodAction(API_ROUTES.paymentMethodById(paymentMethodId))

    if (res.success) {
      toast({
        title: t("payment_methods.removed.title"),
        description: t("payment_methods.removed.description"),
      })
      // Remove the payment method from the local state
      setPaymentMethods((prev) => prev.filter((pm) => pm.id !== paymentMethodId))
      // If the removed payment method was the default, clear the default
      if (defaultPaymentMethodId === paymentMethodId) {
        setDefaultPaymentMethodId(null)
      }
    } else {
      toast({
        title: t("payment_methods.remove_error.title"),
        description: t("payment_methods.remove_error.description"),
        variant: "destructive",
      })
    }
  }

  const handleSetDefaultPaymentMethod = (paymentMethodId: string) => {
    setDefaultPaymentMethod({ paymentMethodId }, (res) => {
      if (res?.success) {
        setDefaultPaymentMethodId(paymentMethodId)
        toast({
          title: t("payment_methods.default_set.title"),
          description: t("payment_methods.default_set.description"),
        })
      }
    })
  }

  const formatPaymentMethod = (paymentMethod: Stripe.PaymentMethod) => {
    if (paymentMethod.type === "card" && paymentMethod.card) {
      const { brand, last4, exp_month, exp_year } = paymentMethod.card
      return {
        type: "card",
        displayName: `${brand?.toUpperCase()} •••• ${last4}`,
        expiry: `${exp_month}/${exp_year}`,
        brand: brand?.toUpperCase(),
        last4,
      }
    }
    return {
      type: paymentMethod.type,
      displayName: paymentMethod.type.toUpperCase(),
      expiry: "",
      brand: "",
      last4: "",
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="h-4 w-32 animate-pulse rounded bg-muted" />
        <div className={horizontal ? "flex gap-3 overflow-x-auto pb-2" : "space-y-2"}>
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className={`animate-pulse rounded-md bg-muted ${horizontal ? "h-24 w-48 shrink-0" : "h-16"}`}
            />
          ))}
        </div>
      </div>
    )
  }

  if (paymentMethods.length === 0) {
    return (
      <div className="text-center">
        <p className="text-sm text-muted-foreground">{t("payment_methods.empty")}</p>
      </div>
    )
  }

  const containerClass = horizontal ? "flex gap-3 overflow-x-auto pb-2 no-scrollbar" : "space-y-2"

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium">{t("payment_methods.title")}</h3>
      <div className={containerClass}>
        {paymentMethods.map((paymentMethod) => {
          const formatted = formatPaymentMethod(paymentMethod)
          const isSelected = selectedPaymentMethodId === paymentMethod.id
          const isDefault = defaultPaymentMethodId === paymentMethod.id

          return (
            <Card
              key={paymentMethod.id}
              className={`cursor-pointer transition-colors hover:bg-muted/50 ${
                horizontal ? "w-48 shrink-0" : ""
              } ${isSelected ? "border-primary bg-primary/5" : ""}`}
              onClick={() => handlePaymentMethodSelect(paymentMethod.id)}
            >
              <CardContent className={`flex items-center justify-between p-4 ${horizontal ? "h-24" : ""}`}>
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <CreditCard className="size-5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{formatted.displayName}</p>
                    {formatted.expiry && (
                      <p className="text-sm text-muted-foreground">
                        {t("payment_methods.expires")} {formatted.expiry}
                      </p>
                    )}
                    {isDefault && (
                      <p className="mt-1 flex items-center gap-1 text-xs font-medium text-yellow-600">
                        <Star className="size-3 fill-yellow-500 text-yellow-500" />
                        {t("payment_methods.default")}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {isSelected && <div className="size-2 rounded-full bg-primary" />}
                  {showDefaultButton && !isDefault && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleSetDefaultPaymentMethod(paymentMethod.id)
                      }}
                      className="text-muted-foreground hover:text-primary"
                      title={t("payment_methods.set_default")}
                    >
                      <StarOff className="size-4" />
                    </Button>
                  )}
                  {showDefaultButton && isDefault && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-yellow-600"
                      disabled
                      title={t("payment_methods.is_default")}
                    >
                      <Star className="size-4 fill-yellow-500 text-yellow-500" />
                    </Button>
                  )}
                  {showRemoveButton && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        handlePaymentMethodRemove(paymentMethod.id)
                      }}
                      className="text-destructive hover:text-destructive"
                      title={t("payment_methods.remove")}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}
